import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Cube } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchFilterBar, { filterRecords } from '@/components/SearchFilterBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProjectCodesPanel = ({ projectCodes, onCreated }) => {
  const { getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ code: '', name: '' });
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(
    () => filterRecords(projectCodes, { keyword, fields: ['code', 'name'] }),
    [projectCodes, keyword]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/project-codes`, formData, { headers: getAuthHeader() });
      toast.success('Project code created successfully');
      setOpen(false);
      setFormData({ code: '', name: '' });
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project code');
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <Button className="bg-accent-brand hover:bg-accent-brand-hover text-white" onClick={() => setOpen(true)} data-testid="create-project-code-button">
          <Plus size={20} className="mr-2" weight="bold" />
          New Project Code
        </Button>
        <div className="flex-1 min-w-[240px] max-w-md">
          <SearchFilterBar
            keyword={keyword} onKeywordChange={setKeyword}
            onClear={() => setKeyword('')}
            placeholder="Search code or name…"
            testIdPrefix="ops-projects-filter"
          />
        </div>
      </div>
      <div className="bg-white border border-border-default rounded">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface border-b border-border-default">
              <tr>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Code</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Name</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Status</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((pc) => (
                <tr key={pc.id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`project-code-${pc.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent-brand-subtle flex items-center justify-center">
                        <Cube size={20} className="text-accent-brand" weight="duotone" />
                      </div>
                      <span className="font-mono font-semibold text-text-primary">{pc.code}</span>
                    </div>
                  </td>
                  <td className="p-4 text-text-secondary">{pc.name}</td>
                  <td className="p-4">
                    <span className="status-badge status-approved">{pc.status}</span>
                  </td>
                  <td className="p-4 text-text-secondary">{new Date(pc.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Project Code</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Project Code</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="e.g., PRJ-001"
                required
                data-testid="project-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Project Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Cancer Research Phase 2"
                required
                data-testid="project-name-input"
              />
            </div>
            <Button type="submit" className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white" data-testid="submit-project-code-button">
              Create Project Code
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectCodesPanel;
